import { BoxPreviewContext } from '../BoxPage/BoxPreviewContext';
import { isSeason } from '@src/theme/season';
import { waitForPreviewAssets } from './waitForPreviewAssets';
import { DigitCounter } from '@src/components/DigitCounter/DigitCounter';
import { Landscape } from '@src/components/Landscape/Landscape';
import { DayNightToggle } from '@src/components/DayNightToggle/DayNightToggle';
import { trackThemeDayNightClick, type ThemeAnalyticsContext } from '@src/utils/themeAnalytics';
import { useEffect, useLayoutEffect, useRef, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { BoxMode, BoxTheme, CardInfo, type BoxInfo } from '@lub0v/santa-utils';
import { BellIcon } from '@src/components/icons/BellIcon';
import { UserIcon } from '@src/components/icons/UserIcon';
import { AccountChip, GiftIcon } from '@src/components';
import { NotificationsProvider } from '@src/components/Notifications/NotificationsContext';
import { BoxGameShell } from '../BoxPage/full/BoxGameShell';
import { FullBoxFrame } from '../BoxPage/full/FullBoxFrame';
import { BoxMainPage } from '../BoxPage/full/BoxMainPage';
import { useTheme } from '@src/theme/useTheme';
import './ThemePreviewPage.css';

export interface ThemePreviewDraft {
  requestId?: string;
  theme: BoxTheme;
  context: ThemeAnalyticsContext;
  name: string;
  picture: string;
  logo: string | null;
  language: string;
  sceneOnly?: boolean;
  giftPreview?: boolean;
  showModeToggle?: boolean;
  mode?: 'light' | 'dark';
}

/** Runs in its own document: viewport queries and fixed navigation behave exactly as in a game. */
export function ThemePreviewPage() {
  const [draft, setDraft] = useState<ThemePreviewDraft | null>(null);
  const { setSeasonOverride, seasonPackage, theme, setTheme } = useTheme();
  const { i18n } = useTranslation();
  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== window.parent) return;
      if (event.data?.type !== 'santa-theme-preview') return;
      const next = event.data.draft as ThemePreviewDraft;
      if (
        !next ||
        (next.context !== 'creation' && next.context !== 'settings' && next.context !== 'profile') ||
        !isSeason(next.theme) ||
        typeof next.name !== 'string' ||
        typeof next.picture !== 'string'
      )
        return;
      setDraft((previous) =>
        previous &&
        previous.requestId === next.requestId &&
        previous.theme === next.theme &&
        previous.context === next.context &&
        previous.name === next.name &&
        previous.picture === next.picture &&
        previous.logo === next.logo &&
        previous.language === next.language &&
        previous.mode === next.mode &&
        previous.sceneOnly === next.sceneOnly &&
        previous.giftPreview === next.giftPreview &&
        previous.showModeToggle === next.showModeToggle
          ? previous
          : next,
      );
      setSeasonOverride(next.theme);
      if (i18n.language !== next.language) void i18n.changeLanguage(next.language);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape')
        window.parent.postMessage({ type: 'santa-theme-preview-close' }, window.location.origin);
    };
    window.addEventListener('message', receive);
    window.addEventListener('keydown', key);
    window.parent.postMessage({ type: 'santa-theme-preview-ready' }, window.location.origin);
    return () => {
      window.removeEventListener('message', receive);
      window.removeEventListener('keydown', key);
    };
  }, [i18n, setSeasonOverride]);
  useEffect(() => {
    if (draft && seasonPackage.id === draft.theme) {
      // Update React state as well as CSS so raster layers select the same mode.
      if (draft.mode && theme !== draft.mode) {
        setTheme(draft.mode, { persist: false });
        return;
      }
      let cancelled = false;
      window.parent.postMessage({ type: 'santa-theme-preview-loading', requestId: draft.requestId }, window.location.origin);
      const assets = draft.giftPreview
        ? Promise.all([...document.querySelectorAll('.landscape, .box-landscape-gift')].map(root => waitForPreviewAssets(root)))
        : waitForPreviewAssets();
      // A missing decorative asset must not leave the adjustment view permanently covered.
      const ready = draft.giftPreview ? Promise.race([assets.catch(() => undefined), new Promise(resolve => window.setTimeout(resolve, 4000))]) : assets;
      void ready.then(() => {
        if (!cancelled) {
          const gift = document.querySelector('.box-landscape-gift')?.getBoundingClientRect();
          window.parent.postMessage({ type: 'santa-theme-preview-rendered', requestId: draft.requestId,
            giftBounds: gift ? { x: gift.x, y: gift.y, width: gift.width, height: gift.height } : null,
          }, window.location.origin);
        }
      }).catch(error => { console.warn('Theme preview assets failed', error); /* Keep covered; the parent offers retry after its timeout. */ });
      return () => { cancelled = true; };
    }
  }, [draft, seasonPackage.id, theme, setTheme]);
  useEffect(() => {
    if (!draft?.giftPreview) return;
    const adjust = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== window.parent || event.data?.type !== 'santa-gift-preview-adjust') return;
      const { giftX, giftY, giftScale } = event.data.preset ?? {};
      if (![giftX, giftY, giftScale].every(Number.isFinite)) return;
      document.querySelectorAll<HTMLElement>('.box-landscape-gift').forEach(gift => {
        gift.style.translate = `${giftX}px ${giftY}px`;
        gift.style.scale = String(giftScale);
      });
    };
    window.addEventListener('message', adjust);
    return () => window.removeEventListener('message', adjust);
  }, [draft?.giftPreview]);
  if (!draft || seasonPackage.id !== draft.theme) return null;
  return (
    <>
      <PreviewGame draft={draft} />
      {!draft.sceneOnly && draft.showModeToggle !== false && (
        <DayNightToggle
          className="theme-preview-mode"
          value={draft.mode ?? (document.documentElement.dataset.theme === 'light' ? 'light' : 'dark')}
          onChange={(mode) => {
            trackThemeDayNightClick(draft.theme, draft.context);
            setDraft(previous => previous ? { ...previous, mode } : previous);
          }}
        />
      )}
    </>
  );
}

function PreviewGame({ draft }: { draft: ThemePreviewDraft }) {
  const { t } = useTranslation();
  const participantsRef = useRef<HTMLElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const root = previewRef.current;
    if (!root) return;
    const makeReadOnly = () => {
      root
        .querySelectorAll<HTMLElement>('a,button,input,select,textarea,[tabindex],[role="button"]')
        .forEach((element) => {
          element.removeAttribute('href');
          element.inert = true;
          element.tabIndex = -1;
        });
    };
    makeReadOnly();
    // Responsive layouts can mount new controls after the initial render.
    const observer = new MutationObserver(makeReadOnly);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  const [cards] = useState(() => {
    const names = draft.language.startsWith('ru')
      ? ['Саша', 'Маша', 'Дима', 'Катя', 'Аня', 'Миша', 'Лена', 'Артём']
      : ['Alex', 'Jamie', 'Sam', 'Taylor', 'Morgan', 'Robin', 'Casey', 'Jordan'];
    return names
      .map((name) => ({ name, order: Math.random() }))
      .sort((a, b) => a.order - b.order)
      .slice(0, 6)
      .map(({ name }, index) =>
        Object.assign(new CardInfo(), {
          id: index + 1,
          username: name,
          randomKey: `preview-${index}`,
          picture: '',
          isVerified: true,
          isYou: false,
          isYourWard: false,
          isWardKnown: false,
          isGiftReceived: false,
          isGiftOnItsWay: false,
        }),
      );
  });
  const info: BoxInfo = {
    ...(draft.giftPreview ? { gift: { id: 0, openedAt: null, message: null } } : {}),
    box: {
      key: 'theme-preview',
      name: draft.name,
      picture: draft.picture,
      logo: draft.logo ?? '',
      theme: draft.theme,
      isPremium: false,
      mode: BoxMode.FULL,
      year: String(new Date().getFullYear()),
      inviteKey: '',
      usePost: false,
      useCashLimit: false,
      cashLimit: 0,
      cashLimitMin: 0,
      cashLimitCurrency: 'usd',
      useWish: true,
      useCircleDraw: false,
      isInviteAfterDraw: false,
      isArchived: false,
      useNames: true,
      isPhoneRequired: false,
      isSupported: false,
    },
    admin: { username: cards[0].username, email: '' },
    isAdmin: true,
    isInvited: false,
    isParticipant: false,
    inviteLink: '',
    isActiveDraw: false,
    participantsNumber: cards.length,
    canCreateCards: false,
    cardId: 0,
    cardVerified: false,
    wardId: 0,
    isSupported: false,
    boxSupporters: [],
  };
  if (draft.sceneOnly) {
    const sampleCount = { winter: 24, halloween: 18, 'cherry-blossom': 32, pixel: 16, egypt: 28, candy: 36, alpine: 42 }[draft.theme];
    return <div ref={previewRef} className="theme-preview-readonly theme-preview-readonly--miniature">
      <Landscape appearance="clear" parallax={false} foreground={draft.theme === 'pixel' ? <DigitCounter value={sampleCount} label="" className="theme-preview-embedded-counter" /> : undefined} />
      {draft.theme !== 'pixel' && <div className="theme-preview-thumbnail-counter"><DigitCounter value={sampleCount} label="" /></div>}
    </div>;
  }
  const blockInteraction = (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };
  // Stop actions at the boundary; the shared components remain identical to the live page.
  // Scrolling stays enabled, but no sample control can navigate, open dialogs or call an API.
  return (
    <BoxPreviewContext.Provider value={true}>
    <NotificationsProvider>
      <div
        ref={previewRef}
        className={`theme-preview-readonly${draft.giftPreview ? ' theme-preview-readonly--gift' : ''}${draft.showModeToggle === false ? ' theme-preview-readonly--miniature theme-preview-readonly--embedded' : ' theme-preview-readonly--full'}`}
        onClickCapture={blockInteraction}
        onAuxClickCapture={blockInteraction}
        onDoubleClickCapture={blockInteraction}
        onContextMenuCapture={blockInteraction}
        onDragStartCapture={blockInteraction}
        onSubmitCapture={blockInteraction}
        onKeyDownCapture={(event) => {
          if (
            (event.key === 'Enter' || event.key === ' ') &&
            (event.target as Element).closest('a,button,input,select,textarea,[role="button"]')
          ) {
            blockInteraction(event);
          }
        }}
      >
        <BoxGameShell
          info={info}
          cards={cards}
          main
          sceneOnly={draft.sceneOnly}
          headerTools={draft.sceneOnly ? (
            <>
              <AccountChip icon={<GiftIcon />} labelHidden label={draft.name} testId="preview-header-game">{draft.name}</AccountChip>
              <AccountChip icon={<BellIcon />} labelHidden label={t('notifications.title')} testId="preview-header-notifications">{t('notifications.title')}</AccountChip>
              <AccountChip icon={<UserIcon />} labelHidden label={t('header.account')} testId="preview-header-account">{t('header.account')}</AccountChip>
            </>
          ) : <></>}
        >
          <FullBoxFrame
            info={info}
            cards={cards}
            route={{ key: info.box.key, view: 'home', card: false }}
            onChanged={() => {}}
            participantsRef={participantsRef}
          >
            <BoxMainPage
              overviewOnly={draft.sceneOnly}
              info={info}
              cards={cards}
              onChanged={() => {}}
              participantsRef={participantsRef}
            />
          </FullBoxFrame>
        </BoxGameShell>
      </div>
    </NotificationsProvider>
    </BoxPreviewContext.Provider>
  );
}
